import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, SearchIcon, Trash2Icon, Loader2Icon, LayoutTemplateIcon, TagIcon, CalendarIcon, ChevronRightIcon } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formTemplatesLibraryApi, FormTemplateRecord } from '../services/creatorApi';
import { toast } from 'sonner';
export function FormTemplatesLibrary() {
  const navigate = useNavigate();
  const {
    currentWorkspace
  } = useWorkspace();
  const [templates, setTemplates] = useState<FormTemplateRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  useEffect(() => {
    loadTemplates();
  }, [currentWorkspace.name]);
  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const all = await formTemplatesLibraryApi.list();
      const filtered = all.filter(t => !t.Team || t.Team === currentWorkspace.name);
      setTemplates(filtered);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to load form templates', {
        description: msg
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      await formTemplatesLibraryApi.delete(id);
      setTemplates(prev => prev.filter(t => t.ID !== id));
      toast.success('Form template deleted');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to delete template', {
        description: msg
      });
    } finally {
      setIsDeleting(false);
      setDeleteConfirm(null);
    }
  };
  const filtered = templates.filter(t => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (t.Template_Name || '').toLowerCase().includes(q) || (t.Description || '').toLowerCase().includes(q) || (t.Category || '').toLowerCase().includes(q);
  });
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-4497">
        <Loader2Icon className="h-8 w-8 text-blue-500 animate-spin" data-id="element-4498" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-4499">
          Loading form templates...
        </span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-4500">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4501">
        <div data-id="element-4502">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4503">
            Form Templates
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4504">
            Reusable form templates you can use when creating new signup forms.
          </p>
        </div>
        <button onClick={() => navigate('/form-templates/new')} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-4505">
          <PlusIcon className="h-4 w-4" data-id="element-4506" />
          New Template
        </button>
      </div>

      {templates.length > 0 && <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-lg p-4" data-id="element-4507">
          <div className="relative flex-1 min-w-0" data-id="element-4508">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4509" />
            <input type="text" placeholder="Search templates..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-4510" />
          </div>
          <span className="text-sm text-gray-500 flex-shrink-0" data-id="element-4511">
            {filtered.length} template{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>}

      {filtered.length === 0 ? <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-4512">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-4513">
            <LayoutTemplateIcon className="h-8 w-8 text-gray-400" data-id="element-4514" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-4515">
            {searchQuery ? 'No matching templates' : 'No form templates yet'}
          </h3>
          <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto" data-id="element-4516">
            {searchQuery ? 'Try adjusting your search.' : 'Create reusable form templates to speed up building new signup forms.'}
          </p>
          {!searchQuery && <button onClick={() => navigate('/form-templates/new')} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-4517">
              <PlusIcon className="h-4 w-4" data-id="element-4518" />
              Create First Template
            </button>}
        </div> : <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-4519">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-4520">
            <thead className="bg-gray-50" data-id="element-4521">
              <tr data-id="element-4522">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4523">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4524">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4525">
                  Mode
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4526">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-4527">
              {filtered.map(tpl => <tr key={tpl.ID} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/form-templates/${tpl.ID}`)} data-id="element-4528">
                  <td className="px-6 py-4" data-id="element-4529">
                    <div className="flex items-center gap-3" data-id="element-4530">
                      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center" data-id="element-4531">
                        <LayoutTemplateIcon className="h-4 w-4 text-indigo-600" data-id="element-4532" />
                      </div>
                      <div className="min-w-0" data-id="element-4533">
                        <p className="text-sm font-medium text-gray-900 truncate" data-id="element-4534">
                          {tpl.Template_Name || 'Untitled'}
                        </p>
                        {tpl.Description && <p className="text-xs text-gray-500 truncate max-w-xs" data-id="element-4535">
                            {tpl.Description}
                          </p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-4536">
                    {tpl.Category ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700" data-id="element-4537">
                        <TagIcon className="h-3 w-3" data-id="element-4538" />
                        {tpl.Category}
                      </span> : <span className="text-xs text-gray-400" data-id="element-4539">—</span>}
                  </td>
                  <td className="px-6 py-4" data-id="element-4540">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${tpl.Builder_Mode === 'builder' ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-4541">
                      {tpl.Builder_Mode === 'builder' ? 'Builder' : 'Code'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-4542">
                    <div className="flex items-center justify-end gap-2" data-id="element-4543">
                      <button onClick={e => {
                  e.stopPropagation();
                  setDeleteConfirm(tpl.ID);
                }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete" data-id="element-4544">
                        <Trash2Icon className="h-4 w-4" data-id="element-4545" />
                      </button>
                      <ChevronRightIcon className="h-4 w-4 text-gray-300" data-id="element-4546" />
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}

      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-4547">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-4548">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-4549" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-4550">
              <div className="flex items-center gap-4" data-id="element-4551">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-4552">
                  <Trash2Icon className="h-6 w-6 text-red-600" data-id="element-4553" />
                </div>
                <div data-id="element-4554">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-4555">
                    Delete Template
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-4556">
                    Are you sure? This cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-4557">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4558">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50" data-id="element-4559">
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}